// src/components/HomePage.tsx
import React, { Suspense, useEffect, useState } from 'react';
import { HomeHeader } from './layout/HomeHeader';
import { NavigationButtons } from './NavigationButtons';
import { LoadingIndicator } from './shared/LoadingIndicator';
import { FirstVisitBanner } from './pwa/FirstVisitBanner';
import { StayDateSelector } from './stay/StayDateSelector';
import { useDevice } from '../hooks/useDevice';
import { useHotelConfig } from '../hooks/useHotelConfig';
import { logger } from '../lib/logger';
import { resolveColor } from '../utils/colorUtils';
import { stayService } from '../lib/stay/stayService';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';

interface HomePageProps {
  onLivechatClick: (url: string) => void;
  onSocialNetworksClick: () => void;
  messageCount: number;
}

export const HomePage: React.FC<HomePageProps> = ({ 
  onLivechatClick, 
  onSocialNetworksClick,
  messageCount 
}) => {
  logger.debug('[HOMEPAGE_INIT] HomePage component mounting', { 
    category: 'Initialization',
    data: {
      timestamp: new Date().toISOString()
    }
  });

  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showStaySelector, setShowStaySelector] = useState(false);
  const { os, isDesktop } = useDevice();
  const { id: hotelId, baseUrl } = useHotelConfig();

  // Fetch config directly
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: () => {
      logger.debug('[HOMEPAGE_CONFIG_FETCH] Fetching config for HomePage', {
        category: 'API',
        data: { 
          hotelId,
          timestamp: new Date().toISOString()
        }
      });
      return configClient.getConfig();
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // Détecter le mode PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    const hasVisited = localStorage.getItem('has_visited');
    if (!hasVisited && !isPWA) {
      logger.debug('Showing install banner', { 
        category: 'PWA',
        data: {
          hotelId,
          baseUrl,
          timestamp: new Date().toISOString()
        }
      });
      setShowInstallBanner(true);
    }
  }, [isPWA, hotelId, baseUrl]);

  // Check if we need to show the stay selector
  useEffect(() => {
    if (config) {
      // Check if we have saved stay dates
      const hasSavedDates = stayService.hasStayDates();
      const areDatesExpired = stayService.areDatesExpired();
      
      logger.debug('[STAY_SELECTOR_CHECK] Checking if stay selector should be shown', {
        category: 'Stay',
        data: { 
          open_with_stay: config.open_with_stay,
          hasSavedDates,
          areDatesExpired,
          open_with_stay_type: typeof config.open_with_stay,
          config_keys: Object.keys(config),
          isCheckoutDay: stayService.isCheckoutDay(),
          hasCheckoutAlertBeenShownToday: stayService.hasCheckoutAlertBeenShownToday(),
          timestamp: new Date().toISOString()
        }
      });
      
      // Show the selector if:
      // 1. open_with_stay is true AND
      // 2. Either there are no saved dates OR the dates are expired
      const shouldShow = config.open_with_stay === true && (!hasSavedDates || areDatesExpired);
      
      logger.debug('[STAY_SELECTOR_DECISION] Stay selector visibility decision', {
        category: 'Stay',
        data: {
          shouldShow,
          open_with_stay: config.open_with_stay,
          hasSavedDates,
          areDatesExpired,
          timestamp: new Date().toISOString()
        }
      });
      
      setShowStaySelector(shouldShow);
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      // Si une image de fond est définie, l'utiliser
      if (config.image) {
        document.body.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${config.image})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundRepeat = 'no-repeat';
        document.body.style.backgroundAttachment = 'fixed';
      } else {
        // Sinon utiliser homeBack-color avec fallback sur medium-color
        const backgroundColor = resolveColor(config['homeBack-color'], config, config['medium-color']);
        document.body.style.backgroundColor = backgroundColor;
        document.body.style.backgroundImage = 'none';
      }

      logger.debug('Home background set', {
        category: 'UI',
        data: {
          hasImage: !!config.image,
          homeBackColor: config['homeBack-color'],
          backgroundColor: resolveColor(config['homeBack-color'], config, config['medium-color']),
          timestamp: new Date().toISOString()
        }
      });
    }

    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundColor = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
    };
  }, [config]);

  if (error) {
    logger.error('Error loading HomePage data', {
      category: 'API',
      data: { 
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      }
    });
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (isLoading || !config) {
    logger.debug('[HOMEPAGE_LOADING] HomePage waiting for config', {
      category: 'General',
      data: { 
        isLoading,
        hasConfig: !!config,
        timestamp: new Date().toISOString()
      }
    });
    return <LoadingIndicator id="homepage-loader" />;
  }

  const handleStayDatesSelected = () => {
    logger.debug('[STAY_DATES_SELECTED] Stay dates have been selected', {
      category: 'Stay',
      data: {
        hasDates: stayService.hasStayDates(),
        timestamp: new Date().toISOString()
      }
    });
    setShowStaySelector(false);
    
    // If this is the first visit, show the PWA installation banner
    if (!localStorage.getItem('has_visited') && !isPWA) {
      setShowInstallBanner(true);
    }
  };

  logger.debug('[HOMEPAGE_RENDER] HomePage ready to render content', { 
    category: 'General',
    data: {
      showStaySelector,
      open_with_stay: config.open_with_stay,
      hasSavedDates: stayService.hasStayDates(),
      areDatesExpired: stayService.areDatesExpired(),
      isCheckoutDay: stayService.isCheckoutDay(),
      hasCheckoutAlertBeenShownToday: stayService.hasCheckoutAlertBeenShownToday(),
      timestamp: new Date().toISOString()
    }
  });
  
  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        backgroundColor: !config.image ? resolveColor(config['homeBack-color'], config, config['medium-color']) : undefined 
      }}
    >
      <HomeHeader 
        config={config}
        title={config.name}
      />
      <div className="home-content flex flex-col min-h-[calc(100vh-50px)]">
        <NavigationButtons 
          onLivechatClick={onLivechatClick} 
          onSocialNetworksClick={onSocialNetworksClick}
          messageCount={messageCount}
        />
      </div>

      {/* Bannière d'installation PWA - Ne s'affiche pas en mode PWA */}
      {showInstallBanner && !isPWA && (
        <FirstVisitBanner
          config={config}
          onClose={() => setShowInstallBanner(false)}
          platform={os === 'iOS' ? 'ios' : 'android'}
        />
      )}

      {/* Sélecteur de dates de séjour - S'affiche si open_with_stay est true et qu'il n'y a pas de dates sauvegardées ou si elles sont expirées */}
      {showStaySelector && (
        <StayDateSelector
          config={config}
          onDatesSelected={handleStayDatesSelected}
          isInitialSetup={true}
          canClose={false}
        />
      )}
    </div>
  );
};